import React, { useMemo, useId } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import {
  Section,
  SectionInner,
  Eyebrow,
  H2,
  Lead,
  BenefitChartsBlock,
  BenefitChartsGrid,
  BenefitChartCard,
  BenefitChartHeader,
  BenefitChartTitle,
  BenefitChartFrame,
  BenefitChartVisual,
  BenefitChartCenter,
  BenefitCornerValues,
  BenefitCornerValue,
  BenefitCornerDot,
  BenefitCornerText,
  BenefitSharedLegend,
  BenefitLegendItem,
  BenefitLegendItemHead,
  BenefitLegendSwatch,
  BenefitLegendName,
} from "./styles";
import { useLanguage } from "../../contexts/LanguageContext";
import { getTranslation } from "../../locales";

/** Paleta alinhada à landing B4 (ciano, verde, lime). */
const SHARE_COLORS = {
  social: "#4ed8ff",
  vert: "#25d07f",
  owner: "#dbfb8e",
};

const CHART_STROKE = "#07131a";

const SHARE_KEYS = ["social", "vert", "owner"];
const SHARE_PERCENTS = { social: 10, vert: 30, owner: 60 };

function formatCornerValue(item, mode) {
  return mode === "percent" ? `${item.value}%` : formatBenefitChartNumber(item.value);
}

function ChartCornerValues({ data, mode }) {
  return (
    <BenefitCornerValues>
      {data.map((item) => (
        <BenefitCornerValue
          key={item.key}
          aria-label={`${item.name}: ${formatCornerValue(item, mode)}`}
        >
          <BenefitCornerDot $color={item.color} aria-hidden />
          <BenefitCornerText $color={item.color}>
            {formatCornerValue(item, mode)}
          </BenefitCornerText>
        </BenefitCornerValue>
      ))}
    </BenefitCornerValues>
  );
}

function formatBenefitChartNumber(value) {
  if (value == null || !Number.isFinite(Number(value))) return "0";

  const rounded = Math.round(Number(value));
  const sign = rounded < 0 ? "-" : "";

  return `${sign}${Math.abs(rounded)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ".")}`;
}

function buildPercentData(labels) {
  return SHARE_KEYS.map((key) => ({
    key,
    name: labels[key],
    value: SHARE_PERCENTS[key],
    color: SHARE_COLORS[key],
  }));
}

function buildCreditsData(labels, totalCredits) {
  return SHARE_KEYS.map((key) => ({
    key,
    name: labels[key],
    value: (totalCredits * SHARE_PERCENTS[key]) / 100,
    color: SHARE_COLORS[key],
  }));
}

function buildLegendItems(labels) {
  return SHARE_KEYS.map((key) => ({
    key,
    name: labels[key],
    color: SHARE_COLORS[key],
  }));
}

const BenefitTooltip = ({ active, payload, mode }) => {
  if (!active || !payload?.length) return null;
  const item = payload[0].payload;

  return (
    <div
      style={{
        background: "rgba(7, 19, 26, 0.96)",
        border: "1px solid rgba(78, 216, 255, 0.22)",
        borderRadius: 10,
        padding: "0.6rem 0.8rem",
        color: "#f5fbff",
        fontSize: "0.84rem",
        boxShadow: "0 8px 24px rgba(0, 0, 0, 0.4)",
      }}
    >
      <div style={{ fontWeight: 600, color: item.color, marginBottom: 2 }}>
        {item.name}
      </div>
      <div style={{ color: "#a9c0cc" }}>
        {mode === "percent"
          ? `${item.value}%`
          : formatBenefitChartNumber(item.value)}
      </div>
    </div>
  );
};

function BenefitDonutChart({
  data,
  chartId,
  mode,
  centerPrimary,
  centerSecondary,
}) {
  return (
    <BenefitChartFrame>
      <ChartCornerValues data={data} mode={mode} />

      <BenefitChartVisual>
        <BenefitChartCenter>
          <strong>{centerPrimary}</strong>
          {centerSecondary ? <span>{centerSecondary}</span> : null}
        </BenefitChartCenter>

        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius="58%"
              outerRadius="84%"
              paddingAngle={3}
              cornerRadius={6}
              stroke={CHART_STROKE}
              strokeWidth={2}
              isAnimationActive
              animationDuration={500}
            >
              {data.map((entry) => (
                <Cell key={`${chartId}-${entry.key}`} fill={entry.color} />
              ))}
            </Pie>

            <Tooltip
              content={<BenefitTooltip mode={mode} />}
              wrapperStyle={{ outline: "none", zIndex: 2 }}
            />
          </PieChart>
        </ResponsiveContainer>
      </BenefitChartVisual>
    </BenefitChartFrame>
  );
}

export default function B4BenefitSharingSection({ totalCredits, loading }) {
  const { language } = useLanguage();
  const t = getTranslation(language).benefitSharing;
  const percentChartId = useId().replace(/:/g, "");
  const creditsChartId = useId().replace(/:/g, "");

  const percentData = useMemo(() => buildPercentData(t.labels), [t.labels]);
  const creditsData = useMemo(
    () => buildCreditsData(t.labels, totalCredits),
    [t.labels, totalCredits]
  );
  const legendItems = useMemo(() => buildLegendItems(t.labels), [t.labels]);

  const formattedTotal = loading ? "…" : formatBenefitChartNumber(totalCredits);

  return (
    <Section id="compartilhamento-beneficios">
      <SectionInner>
        <Eyebrow>{t.eyebrow}</Eyebrow>
        <H2>{t.title}</H2>
        <Lead style={{ maxWidth: "44rem", marginBottom: "2rem" }}>
          {t.subtitle}
        </Lead>

        <BenefitChartsBlock>
          <BenefitChartsGrid>
            <BenefitChartCard>
              <BenefitChartHeader>
                <BenefitChartTitle>{t.percentChartTitle}</BenefitChartTitle>
              </BenefitChartHeader>
              <BenefitDonutChart
                data={percentData}
                chartId={`percent-${percentChartId}`}
                mode="percent"
                centerPrimary="100%"
                centerSecondary={t.centerDistribution}
              />
            </BenefitChartCard>

            <BenefitChartCard>
              <BenefitChartHeader>
                <BenefitChartTitle>{t.creditsChartTitle}</BenefitChartTitle>
              </BenefitChartHeader>
              <BenefitDonutChart
                data={creditsData}
                chartId={`credits-${creditsChartId}`}
                mode="credits"
                centerPrimary={formattedTotal}
                centerSecondary={t.creditsUnit}
              />
            </BenefitChartCard>
          </BenefitChartsGrid>

          <BenefitSharedLegend aria-label={t.legendAria || t.title}>
            {legendItems.map((item) => (
              <BenefitLegendItem key={item.key}>
                <BenefitLegendItemHead>
                  <BenefitLegendSwatch $color={item.color} aria-hidden />
                  <BenefitLegendName>{item.name}</BenefitLegendName>
                </BenefitLegendItemHead>
              </BenefitLegendItem>
            ))}
          </BenefitSharedLegend>
        </BenefitChartsBlock>
      </SectionInner>
    </Section>
  );
}
